import { Link, useParams } from 'react-router-dom';
import { useFinding, useProjects, useReport } from '../api/queries';
import type { Decision, Finding, FindingDetail } from '../api/types';
import { useSession } from '../auth/session';
import { EpisodeTimeline } from '../components/EpisodeTimeline';
import { EvidenceSections } from '../components/Evidence';
import { Prose, ExplanationBlock } from '../components/Prose';
import { QueryState } from '../components/States';
import { StatusBadge } from '../components/StatusBadge';
import { revealInStrip } from '../lib/focus';
import { asList, decisionMeta, formatDate, formatDateTime, statusMeta, themeLabel } from '../lib/format';
import { Discussion } from './finding/Discussion';
import { ReviewPanel } from './finding/ReviewPanel';

const SECTIONS = [
  ['episode', 'Episode'],
  ['evidence', 'Evidence'],
  ['method', 'Scope & method'],
  ['reading', 'Interpretation'],
  ['uncertainty', 'Uncertainty'],
  ['history', 'History'],
  ['discussion', 'Discussion'],
] as const;

function StatusNotice({ finding }: { finding: Finding }) {
  if (finding.status === 'reviewed' && !finding.needsReview) return null;
  const text =
    finding.status === 'proposed'
      ? 'This is a proposed finding. It was drafted from the evidence and has not been reviewed by a steward. Treat it as a draft you can challenge.'
      : finding.status === 'contested'
        ? 'A steward recorded this finding as contested. The disagreement is unresolved — read the decision reason and discussion before relying on it.'
        : finding.status === 'superseded'
          ? 'This finding has been superseded. It is kept for history; do not cite it as current.'
          : 'Reviewed, but new material or a member challenge arrived after review. A steward needs to look again.';
  return (
    <div className={`notice notice--${finding.needsReview && finding.status === 'reviewed' ? 'flag' : finding.status}`} role="note">
      <p>{text}</p>
      {finding.needsReview && finding.status !== 'reviewed' && (
        <p className="notice__extra">It is also flagged: a challenge, correction or changed source is waiting for review.</p>
      )}
    </div>
  );
}

const COMPARED: (keyof Finding)[] = ['title', 'summary', 'scope', 'method', 'interpretation', 'uncertainty', 'implications', 'counterexamples', 'evidence', 'status'];

function changedFields(previous: Finding | undefined, current: Finding): string[] {
  if (previous === undefined) return [];
  return COMPARED.filter((field) => JSON.stringify(previous[field]) !== JSON.stringify(current[field])).map((field) =>
    field === 'counterexamples' ? 'counterexamples' : field,
  );
}

function reviewerLabel(decision: Decision, selfId: string): string {
  if (decision.reviewerId === selfId) return 'You';
  if (decision.reviewerId === 'anonymous') return 'A steward (name withheld by preference)';
  return 'A steward';
}

function History({ detail, selfId }: { detail: FindingDetail; selfId: string }) {
  const versions = [...detail.versions].sort((a, b) => b.version - a.version);
  const byVersion = new Map(detail.versions.map((version) => [version.version, version]));
  const decisionFor = new Map(detail.decisions.map((decision) => [decision.toVersion, decision]));
  if (versions.length === 0) return <p className="muted">No earlier versions are visible to your account.</p>;
  return (
    <ol className="history">
      {versions.map((version) => {
        const decision = decisionFor.get(version.version);
        const changes = changedFields(byVersion.get(version.version - 1), version);
        return (
          <li key={version.version} className={`history__item${version.version === detail.finding.version ? ' history__item--current' : ''}`}>
            <p className="history__head">
              <span className="history__version">Version {version.version}</span>
              <StatusBadge status={version.status} />
              {version.version === detail.finding.version && <span className="tag">Current</span>}
            </p>
            {decision === undefined ? (
              <p className="muted">
                {version.version === 1 ? 'Proposed from the evidence. No steward decision yet.' : 'No decision record is visible for this version.'}
              </p>
            ) : (
              <div className="history__decision">
                <p>
                  <strong>{decisionMeta[decision.decision].verb}</strong> by {reviewerLabel(decision, selfId)} on{' '}
                  {formatDateTime(decision.createdAt)}, from version {decision.fromVersion}.
                </p>
                <blockquote className="history__reason">{decision.reason}</blockquote>
              </div>
            )}
            {changes.length > 0 && <p className="history__changes">Changed: {changes.join(', ')}</p>}
          </li>
        );
      })}
    </ol>
  );
}

function FindingView({ detail }: { detail: FindingDetail }) {
  const session = useSession();
  const report = useReport();
  const projects = useProjects();
  const { finding } = detail;
  const chapterIndex = report.data?.chapters.findIndex((chapter) => chapter.id === finding.chapter) ?? -1;
  const chapter = chapterIndex >= 0 ? report.data?.chapters[chapterIndex] : undefined;
  const project = projects.data?.items.find((candidate) => candidate.id === finding.projectId);
  const counterexamples = asList(finding.counterexamples);

  return (
    <article className="finding" aria-labelledby="finding-title">
      <header className="finding__header">
        <nav aria-label="Breadcrumb" className="breadcrumb">
          <ol>
            <li>
              <Link to="/">Report</Link>
            </li>
            {chapter !== undefined && (
              <li>
                <Link to={`/#${chapter.id.replace(/^chapter:/, '')}`}>
                  Chapter {chapterIndex + 1}: {chapter.title}
                </Link>
              </li>
            )}
          </ol>
        </nav>
        <div className="finding__status-line">
          <StatusBadge status={finding.status} needsReview={finding.needsReview} />
          <span className="finding__version">
            Version {finding.version} · {statusMeta[finding.status].label}
          </span>
        </div>
        <h1 id="finding-title" className="finding__title">
          {finding.title}
        </h1>
        <p className="finding__summary">{finding.summary}</p>
        <dl className="finding__facts">
          {project !== undefined && (
            <div>
              <dt>Project</dt>
              <dd>
                <Link to={`/explore?project=${encodeURIComponent(project.id)}`}>{project.title}</Link>
              </dd>
            </div>
          )}
          <div>
            <dt>Themes</dt>
            <dd>
              {finding.themes.length === 0
                ? 'None recorded'
                : finding.themes.map((theme, index) => (
                    <span key={theme}>
                      {index > 0 && ', '}
                      <Link to={`/explore?theme=${encodeURIComponent(theme)}`}>{themeLabel(theme)}</Link>
                    </span>
                  ))}
            </dd>
          </div>
          <div>
            <dt>Sources up to</dt>
            <dd>{formatDate(finding.sourceCutoff)}</dd>
          </div>
          <div>
            <dt>Visible to</dt>
            <dd>{finding.visibility === 'members' ? 'Invited members' : finding.visibility === 'stewards' ? 'Stewards' : 'Owner'}</dd>
          </div>
        </dl>
        <StatusNotice finding={finding} />
      </header>

      <nav className="finding__sections" aria-label="Sections of this finding">
        <ul>
          {SECTIONS.map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`} onFocus={revealInStrip}>
                {label}
              </a>
            </li>
          ))}
          {session.hasRole('steward') && (
            <li>
              <a href="#review" onFocus={revealInStrip}>
                Review
              </a>
            </li>
          )}
        </ul>
      </nav>

      <section id="episode" className="finding__section" aria-labelledby="episode-title">
        <h2 id="episode-title" className="section-title">
          The episode, step by step
        </h2>
        <p className="section-lede">
          What the records show, in the order it can be established. Where the source does not give a date, we say so
          rather than guess.
        </p>
        <EpisodeTimeline episodes={detail.episodes} sources={detail.sources} />
      </section>

      <section id="evidence" className="finding__section" aria-labelledby="evidence-title">
        <h2 id="evidence-title" className="section-title">
          Evidence
        </h2>
        <p className="section-lede">
          Each citation links to the source record. Paraphrases are not quotations, and repeated text is not an
          independent voice.
        </p>
        <EvidenceSections evidence={finding.evidence} sources={detail.sources} />
        <div className="counterexamples">
          <h3 className="evidence-group__title">Counterexamples and limits</h3>
          {counterexamples.length === 0 ? (
            <p className="muted">No counterexamples are recorded for this version.</p>
          ) : (
            <ul className="point-list point-list--limits">
              {counterexamples.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section id="method" className="finding__section finding__section--split" aria-labelledby="method-title">
        <h2 id="method-title" className="section-title">
          Scope and method
        </h2>
        <div className="split">
          <div>
            <h3 className="minor-title">Scope</h3>
            <Prose text={finding.scope} />
          </div>
          <div>
            <h3 className="minor-title">Method</h3>
            <Prose text={finding.method} />
          </div>
        </div>
      </section>

      <section id="reading" className="finding__section" aria-labelledby="reading-title">
        <h2 id="reading-title" className="section-title">
          Interpretation and implications
        </h2>
        <Prose text={finding.interpretation} className="prose--large" />
        <h3 className="minor-title">What this could mean in practice</h3>
        <ExplanationBlock value={finding.implications} empty="No implications are recorded." />
      </section>

      <section id="uncertainty" className="finding__section" aria-labelledby="uncertainty-title">
        <h2 id="uncertainty-title" className="section-title">
          What we do not know
        </h2>
        <ExplanationBlock value={finding.uncertainty} empty="No uncertainty is recorded — treat that as a gap in the draft, not certainty." />
      </section>

      <section id="history" className="finding__section" aria-labelledby="history-title">
        <h2 id="history-title" className="section-title">
          Revision history
        </h2>
        <p className="section-lede">Versions are immutable. Each steward decision records who decided, when and why.</p>
        <History detail={detail} selfId={session.user.id} />
      </section>

      <section id="discussion" className="finding__section" aria-labelledby="discussion-title">
        <h2 id="discussion-title" className="section-title">
          Discussion
        </h2>
        <Discussion detail={detail} />
      </section>

      {session.hasRole('steward') && (
        <section id="review" className="finding__section finding__section--review" aria-labelledby="review-title">
          <h2 id="review-title" className="section-title">
            Steward review
          </h2>
          <ReviewPanel detail={detail} />
        </section>
      )}
    </article>
  );
}

export function FindingPage() {
  const { id = '' } = useParams();
  const query = useFinding(id);
  return (
    <main id="main" tabIndex={-1} className="page page--finding">
      <QueryState query={query} label="the finding">
        {(detail) => <FindingView detail={detail} />}
      </QueryState>
    </main>
  );
}
